import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { RefreshCw, Smartphone, Upload, Wallet } from 'lucide-react';
import { API, toast } from '@/App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { formatUiLabel, formatWalletTransactionLabel } from '@/utils/displayLabels';
import { MOBILE_MONEY_METHOD_IDS, resolvePaymentMethodFlags } from '@/utils/paymentMethodAvailability';
import { UI_FEEDBACK_MESSAGES, UI_NAV_LABELS } from '@/constants/uiLabels';
import { uploadPaymentProof } from '@/utils/paymentProofUpload';

const PAGE_SIZE = 20;
const MAX_PROOF_SIZE_BYTES = 8 * 1024 * 1024;
const ALLOWED_PROOF_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png']);

const BASE_RECHARGE_METHODS = {
  airtel_money: {
    value: 'airtel_money',
    label: 'Airtel Money',
    icon: Smartphone,
  },
  mtn_money: {
    value: 'mtn_money',
    label: 'MTN Mobile Money',
    icon: Smartphone,
  },
  orange_money: {
    value: 'orange_money',
    label: 'Orange Money',
    icon: Smartphone,
  },
  mpesa: {
    value: 'mpesa',
    label: 'M-Pesa',
    icon: Smartphone,
  },
};

export const buildRechargeMethodsFromSettings = (settings) => {
  const methods = [];
  const paymentNumbers = settings?.payment_numbers || {};
  const flags = resolvePaymentMethodFlags(settings);

  [
    ['airtel_money', 'airtel'],
    ['mtn_money', 'mtn'],
    ['orange_money', 'orange'],
    ['mpesa', 'mpesa'],
  ].forEach(([method, key]) => {
    if (!flags.mobile_money || !flags[key]) return;
    methods.push({
      ...BASE_RECHARGE_METHODS[method],
      paymentNumber: paymentNumbers?.[key],
    });
  });

  return methods;
};

const formatAmount = (value) => Number(value || 0).toLocaleString();

export const buildWalletRechargeSuccessMessage = ({ method, rechargeData, responseMessage }) => {
  if (MOBILE_MONEY_METHOD_IDS.includes(method) && (rechargeData?.status === 'pending_validation' || rechargeData?.requires_admin_validation)) {
    return UI_FEEDBACK_MESSAGES.rechargePendingValidation;
  }

  const normalizedMessage = typeof responseMessage === 'string' ? responseMessage.trim() : '';
  return normalizedMessage || 'Recharge initiee';
};

export const buildWalletRechargeErrorMessage = (detail) => {
  if (detail === 'VALIDATION_ERROR') {
    return 'Cette reference de transaction est deja utilisee pour une recharge.';
  }
  if (detail === 'PAYMENT_PROOF_REQUIRED') {
    return 'Une preuve de paiement Mobile Money est obligatoire.';
  }
  if (detail === 'Unsupported wallet recharge method') {
    return 'Cette methode de paiement n est pas disponible pour la recharge du portefeuille.';
  }
  if (detail === 'WALLET_RECHARGE_ALREADY_PENDING_VALIDATION') {
    return 'Une recharge est deja en attente de validation pour ce portefeuille.';
  }
  return detail || 'Erreur lors de la recharge du portefeuille';
};

const WalletPanel = () => {
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, page_size: PAGE_SIZE, total: 0, has_next: false });
  const [page, setPage] = useState(1);
  const [loadingWallet, setLoadingWallet] = useState(false);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [loadingRechargeMethods, setLoadingRechargeMethods] = useState(false);
  const [recharging, setRecharging] = useState(false);
  const [rechargeMethods, setRechargeMethods] = useState([]);
  const [rechargeAmount, setRechargeAmount] = useState('');
  const [rechargeMethod, setRechargeMethod] = useState('');
  const [rechargeTransactionId, setRechargeTransactionId] = useState('');
  const [proofFile, setProofFile] = useState(null);
  const [proofError, setProofError] = useState('');

  const selectedRechargeMethod = useMemo(
    () => rechargeMethods.find((item) => item.value === rechargeMethod) || rechargeMethods[0] || null,
    [rechargeMethod, rechargeMethods]
  );

  const loadWallet = async ({ silent = false } = {}) => {
    setLoadingWallet(true);
    try {
      const response = await axios.get(`${API}/wallet`);
      setWallet(response.data || null);
    } catch (error) {
      if (!silent) {
        toast.error(error.response?.data?.detail || 'Erreur lors du chargement du portefeuille');
      }
      setWallet(null);
    } finally {
      setLoadingWallet(false);
    }
  };

  const loadTransactions = async (targetPage, { silent = false } = {}) => {
    setLoadingTransactions(true);
    try {
      const response = await axios.get(`${API}/wallet/transactions`, {
        params: { page: targetPage, page_size: PAGE_SIZE },
      });
      setTransactions(response.data?.items || []);
      setPagination(response.data?.pagination || { page: targetPage, page_size: PAGE_SIZE, total: 0, has_next: false });
    } catch (error) {
      if (!silent) {
        toast.error(error.response?.data?.detail || 'Erreur lors du chargement des transactions du portefeuille');
      }
      setTransactions([]);
      setPagination({ page: targetPage, page_size: PAGE_SIZE, total: 0, has_next: false });
    } finally {
      setLoadingTransactions(false);
    }
  };

  const loadRechargeMethods = async () => {
    setLoadingRechargeMethods(true);
    try {
      const response = await axios.get(`${API}/settings`);
      const methods = buildRechargeMethodsFromSettings(response.data || {});
      setRechargeMethods(methods);
      setRechargeMethod((currentMethod) => (methods.some((method) => method.value === currentMethod) ? currentMethod : (methods[0]?.value || '')));
    } catch (error) {
      setRechargeMethods([]);
      setRechargeMethod('');
      toast.error('Impossible de charger les methodes de paiement du portefeuille');
    } finally {
      setLoadingRechargeMethods(false);
    }
  };

  useEffect(() => {
    loadWallet();
    loadRechargeMethods();
  }, []);

  useEffect(() => {
    loadTransactions(page);
  }, [page]);

  const handleProofChange = (event) => {
    const file = event.target.files?.[0] || null;
    if (!file) {
      setProofFile(null);
      setProofError('');
      return;
    }
    if (!ALLOWED_PROOF_TYPES.has(String(file.type || '').toLowerCase())) {
      setProofFile(null);
      setProofError('Formats acceptes : JPG, JPEG ou PNG.');
      toast.error('Formats acceptes : JPG, JPEG ou PNG.');
      return;
    }
    if (file.size > MAX_PROOF_SIZE_BYTES) {
      setProofFile(null);
      setProofError('Le fichier ne doit pas depasser 8 MB.');
      toast.error('Le fichier ne doit pas depasser 8 MB.');
      return;
    }
    setProofFile(file);
    setProofError('');
  };

  const rechargeButtonLabel = recharging ? UI_FEEDBACK_MESSAGES.rechargeProcessing : 'Recharger';

  const handleRecharge = async () => {
    if (recharging) {
      return;
    }

    const numericAmount = Number(rechargeAmount);
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      toast.error('Montant de recharge invalide');
      return;
    }
    if (!selectedRechargeMethod) {
      toast.error(UI_FEEDBACK_MESSAGES.noPaymentMethod);
      return;
    }
    if (!proofFile) {
      toast.error('Veuillez ajouter une preuve de paiement Mobile Money');
      return;
    }

    setRecharging(true);
    try {
      const uploadedProof = await uploadPaymentProof({
        file: proofFile,
        context: 'wallet_recharge',
      });
      const response = await axios.post(`${API}/wallet/recharge`, {
        amount: numericAmount,
        method: rechargeMethod,
        transaction_id: rechargeTransactionId.trim() || undefined,
        proof_file_id: uploadedProof?.id,
      });
      const rechargeData = response.data?.data || {};

      toast.success(buildWalletRechargeSuccessMessage({
        method: rechargeMethod,
        rechargeData,
        responseMessage: response.data?.message,
      }));
      setRechargeAmount('');
      setRechargeTransactionId('');
      setProofFile(null);
      setProofError('');
      await Promise.allSettled([
        loadWallet({ silent: true }),
        loadTransactions(page, { silent: true }),
      ]);
    } catch (error) {
      toast.error(buildWalletRechargeErrorMessage(error.response?.data?.detail || error.message));
    } finally {
      setRecharging(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-cyan-600" />
            {UI_NAV_LABELS.wallet} vendeur
          </CardTitle>
          <CardDescription>Solde, recharge Mobile Money et historique des transactions.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="rounded-lg border bg-cyan-50 border-cyan-200 px-4 py-3">
              <p className="text-sm text-gray-600">Solde disponible</p>
              <p className="text-2xl font-semibold text-cyan-700">
                {formatAmount(wallet?.balance)} {wallet?.currency || 'FCFA'}
              </p>
            </div>
            <Button variant="outline" onClick={() => { loadWallet(); loadTransactions(page); }} disabled={loadingWallet || loadingTransactions}>
              <RefreshCw className={`w-4 h-4 mr-2 ${(loadingWallet || loadingTransactions) ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label htmlFor="wallet-recharge-amount">Montant</Label>
              <Input
                id="wallet-recharge-amount"
                type="number"
                min="1"
                value={rechargeAmount}
                onChange={(event) => setRechargeAmount(event.target.value)}
                placeholder="Ex: 10000"
                disabled={recharging}
              />
            </div>
            <div className="md:col-span-2 flex items-end">
              <Button
                className="w-full bg-red-500 hover:bg-red-600"
                onClick={handleRecharge}
                disabled={recharging || !selectedRechargeMethod || !proofFile}
              >
                {rechargeButtonLabel}
              </Button>
            </div>
          </div>

          <div className="space-y-3">
            <Label>Methode de paiement</Label>
            {loadingRechargeMethods ? (
              <div className="text-sm text-gray-500">Chargement des methodes disponibles...</div>
            ) : rechargeMethods.length === 0 ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                {UI_FEEDBACK_MESSAGES.noPaymentMethod}
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {rechargeMethods.map((method) => {
                  const Icon = method.icon || Smartphone;
                  const isSelected = rechargeMethod === method.value;
                  return (
                    <button
                      key={method.value}
                      type="button"
                      disabled={recharging}
                      onClick={() => {
                        setRechargeMethod(method.value);
                      }}
                      className={`rounded-lg border p-4 text-left transition ${
                        isSelected
                          ? 'border-red-300 bg-red-50 shadow-sm'
                          : 'border-gray-200 bg-white hover:border-red-200 hover:bg-red-50/40'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`rounded-full p-2 ${isSelected ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-600'}`}>
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900">{method.label}</p>
                          {method.paymentNumber && (
                            <p className="mt-1 text-xs text-gray-500">Numero: {method.paymentNumber}</p>
                          )}
                          <p className="mt-1 text-xs text-gray-500">Validation admin requise apres upload de la preuve.</p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {selectedRechargeMethod && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="wallet-recharge-transaction-id">Reference de transaction (optionnel)</Label>
                <Input
                  id="wallet-recharge-transaction-id"
                  value={rechargeTransactionId}
                  onChange={(event) => setRechargeTransactionId(event.target.value)}
                  placeholder="Ex: TXN123456789"
                  disabled={recharging}
                />
              </div>
              <div>
                <Label htmlFor="wallet-recharge-proof">Preuve de paiement *</Label>
                <label
                  htmlFor="wallet-recharge-proof"
                  className="mt-1 flex items-center justify-center w-full p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-red-400 hover:bg-red-50 transition-colors"
                >
                  <div className="text-center">
                    <Upload className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">{proofFile ? proofFile.name : 'Televerser une capture'}</p>
                    <p className="text-xs text-gray-500 mt-1">JPG, JPEG ou PNG - 8 MB max</p>
                  </div>
                  <input
                    id="wallet-recharge-proof"
                    type="file"
                    accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                    onChange={handleProofChange}
                    className="hidden"
                  />
                </label>
                {proofError && <p className="text-xs text-red-600 mt-2">{proofError}</p>}
              </div>
            </div>
          )}

          <p className="text-xs text-gray-500">
            Frais de recharge du portefeuille : 1 %. Le credit est ajoute uniquement apres validation admin de la preuve Mobile Money.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle>Transactions du portefeuille</CardTitle>
          <CardDescription>Historique des credits et debits du portefeuille.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loadingTransactions ? (
            <div className="text-sm text-gray-500">Chargement des transactions...</div>
          ) : transactions.length === 0 ? (
            <div className="text-sm text-gray-500">Aucune transaction de portefeuille pour le moment.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Montant</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead>Note</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.map((item) => {
                  const txType = String(item.type || '').toLowerCase();
                  const isDebit = ['delivery_debited', 'subscription_debited'].includes(txType);
                  return (
                    <TableRow key={item.id}>
                      <TableCell>{item.created_at ? new Date(item.created_at).toLocaleString() : '-'}</TableCell>
                      <TableCell>
                        <Badge className={isDebit ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}>
                          {formatWalletTransactionLabel(txType) || '-'}
                        </Badge>
                      </TableCell>
                      <TableCell className={isDebit ? 'text-amber-700 font-medium' : 'text-green-700 font-medium'}>
                        {isDebit ? '-' : '+'}{formatAmount(item.amount)} {wallet?.currency || 'FCFA'}
                      </TableCell>
                      <TableCell className="text-xs text-gray-600">
                        {formatUiLabel(item.reference_type) || '-'}
                        {item.reference_id ? ` / ${item.reference_id}` : ''}
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">{item.note || '-'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}

          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500">Total: {pagination.total || 0}</p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={loadingTransactions || page <= 1}
              >
                Precedent
              </Button>
              <span className="text-sm text-gray-600">Page {page}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((prev) => prev + 1)}
                disabled={loadingTransactions || !pagination.has_next}
              >
                Suivant
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default WalletPanel;

